import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeSecurity } from '../../utils/qrCodeSecurity';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import { QrCode, OpenInNew } from '@mui/icons-material';

export default function MobileScoreTest() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const tournamentsSnapshot = await getDocs(collection(db, 'tournaments'));
      const tournamentsData = tournamentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTournaments(tournamentsData);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTournamentSelect = async (tournament) => {
    setSelectedTournament(tournament);
    setSelectedPlayer(null);
    setQrCode(null);
    
    // Fetch players for this tournament's organization
    try {
      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournament.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);
      console.log('Fetched players for tournament:', playersData.length, playersData);
    } catch (error) {
      console.error('Error fetching players:', error);
      setPlayers([]);
    }
  };

  const handlePlayerSelect = (player) => {
    setSelectedPlayer(player);
    setQrCode(null);
  };

  const generateTestQRCode = async () => {
    if (!selectedTournament || !selectedPlayer) return;

    try {
      setGenerating(true);
      
      // Generate QR code
      const qrCodeData = QRCodeSecurity.generateSecureQRCode(selectedPlayer.id, selectedTournament.id);
      
      // Save to database
      await QRCodeSecurity.saveQRCode(
        selectedTournament.id, 
        selectedPlayer.id, 
        qrCodeData, 
        'test-user@example.com'
      );
      
      setQrCode(qrCodeData.code);
      
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setGenerating(false);
    }
  };

  const openMobileScoring = () => {
    if (qrCode) {
      const url = `/mobile-scoring/${qrCode}`;
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Mobile Score Entry Test
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This tool helps you generate QR codes and test the mobile score entry interface.
      </Alert>

      <Grid container spacing={3}>
        {/* Tournament Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 1: Select Tournament
            </Typography>
            
            {tournaments.length === 0 ? (
              <Alert severity="warning">
                No tournaments found. Create a tournament first.
              </Alert>
            ) : (
              <Box>
                {tournaments.map(tournament => (
                  <Card 
                    key={tournament.id}
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      border: selectedTournament?.id === tournament.id ? 2 : 0,
                      borderColor: 'primary.main'
                    }}
                    onClick={() => handleTournamentSelect(tournament)}
                  >
                    <CardContent>
                      <Typography variant="h6">{tournament.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Organization ID: {tournament.organizationId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tournament ID: {tournament.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {tournament.status || 'Unknown'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Player Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 2: Select Player
            </Typography>
            
            {!selectedTournament ? (
              <Alert severity="info">
                Select a tournament first.
              </Alert>
            ) : players.length === 0 ? (
              <Alert severity="warning">
                <Typography variant="body2" gutterBottom>
                  No players found in this tournament.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Organization ID: {selectedTournament.organizationId}
                </Typography>
                <Typography variant="body2">
                  This might mean there are no players in the organization, or there's a data issue.
                </Typography>
              </Alert>
            ) : (
              <Box>
                {players.map(player => (
                  <Card 
                    key={player.id}
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      border: selectedPlayer?.id === player.id ? 2 : 0,
                      borderColor: 'primary.main'
                    }}
                    onClick={() => handlePlayerSelect(player)}
                  >
                    <CardContent>
                      <Typography variant="h6">
                        {player.firstName} {player.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Handicap: {player.handicap || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {player.id}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* QR Code Generation */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 3: Generate QR Code
            </Typography>
            
            {!selectedTournament || !selectedPlayer ? (
              <Alert severity="info">
                Select a tournament and player first.
              </Alert>
            ) : (
              <Box>
                <Button
                  variant="contained"
                  startIcon={<QrCode />}
                  onClick={generateTestQRCode}
                  disabled={generating}
                  sx={{ mb: 2 }}
                >
                  {generating ? 'Generating...' : 'Generate QR Code'}
                </Button>

                {qrCode && (
                  <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      QR Code generated successfully!
                    </Alert>
                    
                    <Typography variant="body1" gutterBottom>
                      <strong>QR Code:</strong> {qrCode}
                    </Typography>
                    
                    <Typography variant="body1" gutterBottom>
                      <strong>Mobile URL:</strong> {window.location.origin}/mobile-scoring/{qrCode}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<OpenInNew />}
                      onClick={openMobileScoring}
                      sx={{ mt: 2 }}
                    >
                      Open Mobile Score Entry
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
